import React from "react";
import { Circle, Marker, Polyline } from "react-leaflet";
import L from "leaflet";

const deg2rad = (deg) => (deg * Math.PI) / 180;

function pointAtAzimuth(center, radius, azimuth) {
  if (!isValidLatLng(center) || !Number.isFinite(radius) || !Number.isFinite(azimuth)) {
    return [0, 0]; // or null, based on your error handling strategy
  }
  
  const R = 6378137;
  const lat1 = deg2rad(center[0]);
  const lng1 = deg2rad(center[1]);
  const brng = deg2rad(azimuth);

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(radius / R) +
    Math.cos(lat1) * Math.sin(radius / R) * Math.cos(brng)
  );

  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(brng) * Math.sin(radius / R) * Math.cos(lat1),
      Math.cos(radius / R) - Math.sin(lat1) * Math.sin(lat2)
    );

  return [lat2 * (180 / Math.PI), lng2 * (180 / Math.PI)];
}

const isValidLatLng = (center) =>
  Array.isArray(center) &&
  center.length === 2 &&
  Number.isFinite(center[0]) &&
  Number.isFinite(center[1]);

const AzimuthRuler = ({
  center = [-6.369028, 34.888822],
  radius = 100000,
  step = 30,
  ringInterval = 20000,
  beamWidth = 5,
  rotationSpeed = 2,
  threatLevel = "normal",
  color = "0,255,255", // ✅ default radar color
}) => {
  if (!isValidLatLng(center)) return null;

  const lines = [];
  const rings = [];
  const labels = [];

  // Fine azimuth lines (1-degree ticks)
  // for (let az = 0; az < 360; az += 1) {
  //   const end = pointAtAzimuth(center, radius, az);
  //   lines.push(
  //     <Polyline
  //       key={`az-line-${az}`}
  //       positions={[center, end]}
  //       pathOptions={{
  //         color: "#444",
  //         weight: 0.5,
  //         opacity: 0.2,
  //       }}
  //     />
  //   );
  // }

  // Major + minor azimuth lines
  for (let az = 0; az < 360; az += 10) {
    const end = pointAtAzimuth(center, radius, az);

    const isMajorLine = az % 30 === 0; // every 30 degrees
    const weight = isMajorLine ? 1.0 : 0.5;
    const opacity = isMajorLine ? 0.6 : 0.5;

    lines.push(
      <Polyline
        key={`az-line-${az}`}
        positions={[center, end]}
        pathOptions={{
          color: `rgb(${color})`,
          weight,
          dashArray: isMajorLine ? "18 8" : "4 8",
          opacity,
        }}
      />
    );
    
    const labelPos = pointAtAzimuth(center, radius + 20000, az);
    const markerColor = "255,255,0, 0.7";
    labels.push(
      <Marker
        key={`label-${az}`}
        position={labelPos}
        icon={L.divIcon({
          className: "azimuth-label",
          html: `<div style="color: rgba(${markerColor}); font-size: 13px; text-shadow: 0 0 5px red;">${az}°</div>`,
          iconSize: [30, 14],
          iconAnchor: [15, 7],
        })}
        interactive={false}
      />
    );    
  }

  // Range rings and labels
  for (let r = 25000; r <= radius; r += 25000) {
    const isMajor = r % 50000 === 0; // every 50 km major
    const weight = isMajor ? 1.0 : 0.5;
    const opacity = isMajor ? 0.6 : 0.5;
    const dashArray = isMajor ? "8 6" : "4 6";

    rings.push(
      <Circle
        key={`ring-${r}`}
        center={center}
        radius={r}
        pathOptions={{
          color: `rgba(${color},${opacity})`,
          weight,
          dashArray,
          fillOpacity: 0.03,
        }}
      />
    );

    if (isMajor && r < radius) {
      const kmLabel = r / 1000;
      [0, 90, 180, 270].forEach((az) => {
        const pos = pointAtAzimuth(center, r, az);
        labels.push(
          <Marker
            key={`range-label-${az}-${r}`}
            position={pos}
            icon={L.divIcon({
              className: "range-label",
              html: `<div style="color:rgba(${color});font-size:12px;text-shadow:0 0 5px black;">${kmLabel}</div>`,
              iconSize: [30, 14],
              iconAnchor: [15, 7],
            })}
            interactive={false}
          />
        );
      });
    }
  }

  return (
    <>
      {/* Filled base */}
      <Circle
        center={center}
        radius={radius}
        pathOptions={{
          color: `rgb(${color})`,
          fillColor: `rgba(${color}, 0.07)`,
          fillOpacity: 0.07,
          weight: 2.5,
          opacity: 0.6,
        }}
      />

      {/* Bold border */}
      <Circle
        center={center}
        radius={radius}
        pathOptions={{
          color:
            threatLevel === "danger"
              ? "red"
              : threatLevel === "alert"
              ? "orange"
              : `rgb(${color})`,
          weight: 1,
          fill: false,
          opacity: 0.6,
        }}
      />

      {rings}
      {lines}
      {labels}
    </>
  );
};

export default React.memo(AzimuthRuler);
